// Utilidades PURAS do editor de templates (§4.5): extração e
// validação de variáveis {{:var}}, validação de sintaxe HTML/JSON,
// formatação de JSON e substituição de variáveis para o preview.
// Sem dependências de UI — tudo testável em ambiente node/jsdom.
import { html as beautifyHtml } from "js-beautify"

import type { NotificationTemplateVariableType } from "@/types/notification-center"

// Sintaxe das variáveis do motor de templates: {{:nome}}.
const TEMPLATE_VARIABLE_REGEX = /\{\{:\s*([\w.]+)\s*\}\}/g

// Extrai os nomes de variáveis usados no source (únicos, em ordem
// de primeira ocorrência).
export const extractTemplateVariables = (
  source: string
): string[] => {
  const names: string[] = []
  for (const match of source.matchAll(
    TEMPLATE_VARIABLE_REGEX
  )) {
    if (!names.includes(match[1])) {
      names.push(match[1])
    }
  }
  return names
}

// Variáveis usadas no source mas NÃO declaradas no contrato
// (variables[] do GET versions) — viram ⚠ no preview e no rodapé.
export const findUndeclaredVariables = (
  source: string,
  declared: NotificationTemplateVariableType[]
): string[] => {
  const declaredNames = new Set(
    declared.map((variable) => variable.name)
  )
  return extractTemplateVariables(source).filter(
    (name) => !declaredNames.has(name)
  )
}

// Resultado da validação de JSON — linha 1-based do erro quando o
// parser expõe a posição ("… at position N").
export type JsonValidationResult =
  | { valid: true }
  | { valid: false; message: string; line: number | null }

export const validateJsonSource = (
  source: string
): JsonValidationResult => {
  try {
    JSON.parse(source)
    return { valid: true }
  } catch (error) {
    const message =
      error instanceof Error ? error.message : String(error)
    // V8/JSC expõem "at position N" (0-based) ou "line N column M".
    const lineMatch = message.match(/line (\d+)/i)
    if (lineMatch) {
      return {
        valid: false,
        message,
        line: Number(lineMatch[1]),
      }
    }
    const positionMatch = message.match(/position (\d+)/i)
    if (positionMatch) {
      const position = Number(positionMatch[1])
      const line = source
        .slice(0, position)
        .split("\n").length
      return { valid: false, message, line }
    }
    return { valid: false, message, line: null }
  }
}

// Formata JSON preservando as variáveis {{:var}} (que vivem dentro
// de strings e portanto sobrevivem ao parse). null = não formatável.
export const formatJsonSource = (
  source: string
): string | null => {
  try {
    return JSON.stringify(JSON.parse(source), null, 2)
  } catch {
    return null
  }
}

// Formata (pretty-print/indentação) o HTML do template via js-beautify.
// Só reindenta — NÃO altera a semântica (tabelas, divs, estilos inline
// e placeholders {{:campo}} ficam intactos), ao contrário de um WYSIWYG.
// `wrap_line_length: 0` não quebra linhas longas (URLs/estilos inline).
// Retorna null se a entrada for vazia ou o beautify falhar.
export const formatHtmlSource = (
  source: string
): string | null => {
  if (!source.trim()) return null
  try {
    return beautifyHtml(source, {
      indent_size: 2,
      wrap_line_length: 0,
      preserve_newlines: true,
      max_preserve_newlines: 1,
      end_with_newline: false,
    })
  } catch {
    return null
  }
}

// Elementos void do HTML — não exigem fechamento.
const VOID_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
])

export type HtmlValidationResult = {
  valid: boolean
  issues: string[]
}

// Validação BÁSICA de HTML: DOMParser (quando disponível) +
// balanceamento de tags (parser HTML é leniente demais sozinho).
export const validateHtmlSource = (
  source: string
): HtmlValidationResult => {
  const issues: string[] = []
  if (typeof DOMParser !== "undefined") {
    const parsed = new DOMParser().parseFromString(
      source,
      "text/html"
    )
    const parserError = parsed.querySelector("parsererror")
    if (parserError?.textContent) {
      issues.push(parserError.textContent)
    }
  }
  // Balanceamento simples de tags (ignora void/comentários e
  // atributos com ">") — suficiente para erro de digitação comum.
  const stack: string[] = []
  const tagRegex = /<\/?([a-zA-Z][\w-]*)(?:\s[^<>]*)?>/g
  const withoutComments = source.replace(
    /<!--[\s\S]*?-->/g,
    ""
  )
  for (const match of withoutComments.matchAll(tagRegex)) {
    const tagName = match[1].toLowerCase()
    if (VOID_ELEMENTS.has(tagName)) continue
    if (match[0].endsWith("/>")) continue
    if (match[0].startsWith("</")) {
      const lastOpen = stack.lastIndexOf(tagName)
      if (lastOpen === -1) {
        issues.push(`</${tagName}>`)
      } else {
        stack.splice(lastOpen, 1)
      }
    } else {
      stack.push(tagName)
    }
  }
  stack.forEach((tagName) => issues.push(`<${tagName}>`))
  return { valid: issues.length === 0, issues }
}

// Substitui as variáveis do source pelos exemplos do contrato.
// Variáveis NÃO declaradas são marcadas com ⚠ (aparecem no preview
// e são listadas no rodapé de validação).
export const renderTemplatePreview = (
  source: string,
  declared: NotificationTemplateVariableType[]
): string => {
  // O token dentro de {{:...}} é o `path` da variável (ex.:
  // "merchant.fantasyName"), não o `name`. Indexamos por path E por
  // name para resolver ambas as formas usadas nos templates.
  const exampleByToken = new Map<string, string>()
  declared.forEach((variable) => {
    if (variable.path)
      exampleByToken.set(variable.path, variable.example)
    if (variable.name)
      exampleByToken.set(variable.name, variable.example)
  })
  return source.replace(
    TEMPLATE_VARIABLE_REGEX,
    (raw, token: string) => {
      const example = exampleByToken.get(token)
      if (example !== undefined) return example
      return `⚠ ${raw}`
    }
  )
}

// Texto da bolha de WhatsApp: substitui variáveis e, se o source
// for JSON válido, extrai o campo textual mais provável
// (body.text > text > message > body string); senão, o JSON cru.
export const buildWhatsAppPreviewText = (
  source: string,
  declared: NotificationTemplateVariableType[]
): string => {
  const substituted = renderTemplatePreview(
    source,
    declared
  )
  try {
    const parsed: unknown = JSON.parse(substituted)
    if (parsed && typeof parsed === "object") {
      const record = parsed as Record<string, unknown>
      const body = record.body
      if (body && typeof body === "object") {
        const bodyText = (body as Record<string, unknown>)
          .text
        if (typeof bodyText === "string") return bodyText
      }
      if (typeof body === "string") return body
      if (typeof record.text === "string")
        return record.text
      if (typeof record.message === "string")
        return record.message
    }
    return substituted
  } catch {
    return substituted
  }
}

// Validação de e-mail/telefone do dialog de test-send.
export const isValidTestRecipient = (
  channel: string,
  recipient: string
): boolean => {
  if (channel === "EMAIL") {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)
  }
  // WHATSAPP/SMS: E.164 flexível (+ opcional, 10–15 dígitos).
  return /^\+?\d{10,15}$/.test(
    recipient.replace(/[\s()-]/g, "")
  )
}

// Chave de identidade de uma variável nos "Dados de exemplo": o `path`
// (ex.: "customer.firstName") quando existe, senão o `name`. É por essa
// chave que as edições locais de exemplo são indexadas.
export const getSampleVariableKey = (
  variable: NotificationTemplateVariableType
): string => variable.path || variable.name

// Aplica as edições locais dos "Dados de exemplo" sobre as variables do
// contrato: cada override (indexado por getSampleVariableKey) substitui
// o `example` da variável correspondente; as demais são preservadas.
// O resultado alimenta o preview e o JSON reconstruído — como
// renderTemplatePreview indexa por path E name, tanto {{:customer.firstName}}
// quanto {{:firstName}} passam a resolver para o valor editado.
export const applySampleOverrides = (
  variables: NotificationTemplateVariableType[],
  overrides: Record<string, string>
): NotificationTemplateVariableType[] =>
  variables.map((variable) => {
    const override =
      overrides[getSampleVariableKey(variable)]
    return override === undefined
      ? variable
      : { ...variable, example: override }
  })

// Reconstrói o objeto de EXEMPLO do payload da origem a partir das
// variables[] do contrato (name/path/example). Cada `path` (ex.:
// "customer.firstName") vira uma chave aninhada com o `example` como
// folha — é o JSON que o usuário inspeciona para descobrir quais
// campos pode usar como placeholder {{:campo}}.
export const buildExampleObjectFromVariables = (
  variables: NotificationTemplateVariableType[]
): Record<string, unknown> => {
  const root: Record<string, unknown> = {}
  for (const variable of variables) {
    const segments = variable.path
      .split(".")
      .map((segment) => segment.trim())
      .filter(Boolean)
    if (segments.length === 0) continue
    let cursor = root
    segments.forEach((segment, index) => {
      const isLeaf = index === segments.length - 1
      if (isLeaf) {
        cursor[segment] = variable.example
        return
      }
      // Só desce em objeto; se o caminho colidir com uma folha já
      // definida, preserva a folha e não sobrescreve.
      const next = cursor[segment]
      if (
        typeof next !== "object" ||
        next === null ||
        Array.isArray(next)
      ) {
        cursor[segment] = {}
      }
      cursor = cursor[segment] as Record<string, unknown>
    })
  }
  return root
}
